import React from "react";
import { Move, ZoomIn, ZoomOut, Hexagon, Minus, Ruler, Square, Tag, Mountain } from "lucide-react";
import { useT } from "../../lib/lang";

// Iter93 (Phase 1) -> Iter96 (Phase 2B): Planification toolbar.
//
// This array IS the "interface definition": tools with no `action` are
// map INTERACTION MODES (wired to Terra Draw's `setMode` by the parent's
// `onSelectTool`); tools with an `action` key are immediate map commands
// (pan is native drag, so only zoom needs one). `annotation`/`terrain`
// remain disabled placeholders — reserved for a later phase, untouched
// here per the Phase 2B spec's strict scope.
export const PLANIFICATION_TOOLS = [
  { key: "pan", icon: Move, enabled: true, labelKey: "planificationToolPan" },
  { key: "zoom_in", icon: ZoomIn, enabled: true, action: "zoomIn", labelKey: "planificationToolZoomIn" },
  { key: "zoom_out", icon: ZoomOut, enabled: true, action: "zoomOut", labelKey: "planificationToolZoomOut" },
  { key: "draw_polygon", icon: Hexagon, enabled: true, labelKey: "planificationToolPolygon" },
  { key: "draw_line", icon: Minus, enabled: true, labelKey: "planificationToolLine" },
  { key: "measure_distance", icon: Ruler, enabled: true, labelKey: "planificationToolMeasureDistance" },
  { key: "measure_area", icon: Square, enabled: true, labelKey: "planificationToolMeasureArea" },
  { key: "annotation", icon: Tag, enabled: true, labelKey: "planificationToolAnnotation" },
  { key: "terrain", icon: Mountain, enabled: false, labelKey: "planificationToolTerrain" },
];

/**
 * Compact vertical toolbar, overlaid top-left of the map (same
 * positioning convention as MapFullscreenButton on Carte). `activeTool`
 * is local UI state only in Phase 1 (just highlights "pan" — there is
 * nothing else to switch between yet); `onAction("zoomIn"|"zoomOut")`
 * is the only thing that actually touches the map.
 */
export const PlanificationToolbar = ({ activeTool, onSelectTool, onAction }) => {
  const [t] = useT();
  return (
    <div
      className="absolute top-3 left-3 z-[450] flex flex-col bg-[#111418]/90 backdrop-blur border border-zinc-800"
      data-testid="planification-toolbar"
      role="toolbar"
      aria-label={t.planification}
    >
      {PLANIFICATION_TOOLS.map((tool, i) => {
        const Icon = tool.icon;
        const isActive = tool.enabled && !tool.action && activeTool === tool.key;
        const label = t[tool.labelKey] || tool.key;
        const title = tool.enabled ? label : `${label} · ${t.planificationComingSoon}`;
        return (
          <button
            key={tool.key}
            type="button"
            disabled={!tool.enabled}
            data-testid={`planification-tool-${tool.key}`}
            title={title}
            aria-label={label}
            aria-pressed={isActive}
            onClick={() => {
              if (!tool.enabled) return;
              if (tool.action) onAction?.(tool.action);
              else onSelectTool?.(tool.key);
            }}
            className={`p-2 border-b border-zinc-800/60 last:border-b-0 transition-colors ${
              !tool.enabled
                ? "text-zinc-700 cursor-not-allowed"
                : isActive
                  ? "bg-emerald-500 text-black"
                  : "text-zinc-300 hover:bg-zinc-800 hover:text-emerald-300"
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
};
